import * as React from "react";
import { CheckCircle2, Loader2, Smartphone } from "lucide-react";
import { Button } from "@eduviaos/ui-kit";
import { SectionCard, SectionHeader } from "../../components/Section";
import { openRazorpaySubscriptionCheckout } from "../../lib/billing/razorpayCheckout";
import {
  createLearnerAppSubscriptionOrder,
  verifyLearnerAppPayment,
} from "../../lib/enrollment/subscription";
import { usePortalLocale } from "../../lib/i18n/usePortalLocale";

interface PaywallReceipt {
  amountPaise: number;
  paidAt: Date;
  paymentId: string;
}

function formatPrice(paise: number): string {
  const rupees = paise / 100;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: rupees % 1 === 0 ? 0 : 2,
  }).format(rupees);
}

function formatRenewalDate(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
  }).format(new Date(iso));
}

function isOnlinePricingMode(mode: string | null | undefined): boolean {
  return mode === "separate" || mode === "sa_push";
}

/**
 * Shown when the centre charges separately for the learner app and access
 * is not active (Phase SC-4 + SC-21f). Online pay via Razorpay; cash at the
 * front desk remains the secondary path.
 */
export function AppPaywall({
  monthlyPricePaise,
  centreName,
  pricingMode,
  isLapsed = false,
  lapsedPaidUntil = null,
  onAccessGranted,
}: {
  monthlyPricePaise: number;
  centreName: string;
  pricingMode: string | null;
  isLapsed?: boolean;
  lapsedPaidUntil?: string | null;
  onAccessGranted: () => void | Promise<void>;
}) {
  const { strings: s, locale } = usePortalLocale();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [receipt, setReceipt] = React.useState<PaywallReceipt | null>(null);

  const priceLabel = formatPrice(monthlyPricePaise);
  const showPayOnline = isOnlinePricingMode(pricingMode) && monthlyPricePaise >= 100;

  const lapsedDaysAgo = React.useMemo(() => {
    if (!isLapsed || !lapsedPaidUntil) return null;
    const then = new Date(lapsedPaidUntil).getTime();
    const now = Date.now();
    return Math.max(0, Math.floor((now - then) / (24 * 60 * 60 * 1000)));
  }, [isLapsed, lapsedPaidUntil]);

  const handlePayOnline = React.useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const order = await createLearnerAppSubscriptionOrder();
      await openRazorpaySubscriptionCheckout({
        keyId: order.key_id,
        orderId: order.razorpay_order_id,
        amount: order.amount,
        currency: order.currency,
        centreName,
        onDismiss: () => setBusy(false),
        onSuccess: async (response) => {
          try {
            const verified = await verifyLearnerAppPayment(response);
            if (!verified.verified) {
              setError(s.appPaywallVerifyFailed);
              return;
            }
            setReceipt({
              amountPaise: order.amount,
              paidAt: new Date(),
              paymentId: verified.razorpay_payment_id,
            });
          } catch {
            setError(s.appPaywallPaymentFailed);
          } finally {
            setBusy(false);
          }
        },
      });
    } catch {
      setError(s.appPaywallPaymentFailed);
      setBusy(false);
    }
  }, [centreName, s.appPaywallPaymentFailed, s.appPaywallVerifyFailed]);

  const handleContinue = React.useCallback(async () => {
    setReceipt(null);
    await onAccessGranted();
  }, [onAccessGranted]);

  if (receipt) {
    const paidDate = new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(receipt.paidAt);
    const nextAccessDate = formatRenewalDate(
      new Date(receipt.paidAt.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      locale,
    );
    return (
      <div className="lg:mx-auto lg:max-w-lg">
        <SectionHeader title={s.appPaywallReceiptTitle} />
        <SectionCard className="mt-4">
          <div className="flex flex-col items-center gap-4 px-2 py-6 text-center">
            <div className="grid size-14 place-items-center rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="size-7" aria-hidden="true" />
            </div>
            <dl className="grid w-full max-w-sm gap-2 text-left text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-muted-foreground">{s.appPaywallReceiptAmount}</dt>
                <dd className="font-semibold tabular-nums">{formatPrice(receipt.amountPaise)}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-muted-foreground">{s.appPaywallReceiptOn}</dt>
                <dd className="tabular-nums">{paidDate}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-muted-foreground">{s.appPaywallReceiptRef}</dt>
                <dd className="text-muted-foreground truncate font-mono text-xs">
                  {receipt.paymentId}
                </dd>
              </div>
            </dl>
            <p className="text-muted-foreground text-sm">
              {s.appPaywallReceiptNextAccess(nextAccessDate)}
            </p>
            <Button
              type="button"
              className="w-full max-w-sm transition-transform active:scale-[0.97] motion-reduce:transform-none"
              onClick={() => void handleContinue()}
            >
              {s.appPaywallReceiptContinue}
            </Button>
          </div>
        </SectionCard>
      </div>
    );
  }

  const title = isLapsed ? s.appPaywallLapsedTitle : s.appPaywallTitle;
  const body = isLapsed
    ? s.appPaywallLapsedBody(
        centreName,
        lapsedPaidUntil ? formatRenewalDate(lapsedPaidUntil, locale) : "",
      )
    : s.appPaywallBody(centreName);

  return (
    <div className="lg:mx-auto lg:max-w-lg">
      <SectionHeader title={title} />
      <p className="text-muted-foreground mb-2 text-sm">{body}</p>
      {isLapsed && lapsedDaysAgo !== null ? (
        <p className="text-destructive/90 mb-4 text-xs font-medium tabular-nums">
          {s.appPaywallLapsedDaysAgo(lapsedDaysAgo)}
        </p>
      ) : null}
      {error ? (
        <p className="text-destructive mb-4 text-sm" role="alert">
          {error}
        </p>
      ) : null}
      <SectionCard className="mt-4">
        <div className="flex flex-col items-center gap-4 px-2 py-6 text-center">
          <div className="bg-primary/10 text-primary grid size-14 place-items-center rounded-2xl">
            <Smartphone className="size-7" aria-hidden="true" />
          </div>
          <div className="grid w-full max-w-sm gap-3">
            <p className="text-lg font-semibold">
              {priceLabel}
              {s.monthlySuffix}
            </p>
            {showPayOnline ? (
              <Button
                type="button"
                className="w-full transition-transform active:scale-[0.97] motion-reduce:transform-none"
                disabled={busy}
                onClick={() => void handlePayOnline()}
              >
                {busy ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" aria-hidden="true" />
                    {s.appPaywallPaying}
                  </>
                ) : (
                  s.appPaywallPayNow
                )}
              </Button>
            ) : null}
            <p className="text-muted-foreground text-sm leading-relaxed">
              {showPayOnline ? s.appPaywallPayAtCentreSecondary : s.appPaywallPayAtCentre}
            </p>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
